<template>
  <div class="dashboard-container">
    <!-- Full-screen Background Map -->
    <div class="map-layer">
      <component 
        :is="currentMapComponent" 
        ref="mapRef" 
        :stations="filteredStations" 
        :is3D="baseMapMode === 'outline3d'"
        @province-click="handleProvinceClick"
        @station-click="handleStationClick" 
        @cluster-click="handleClusterClick"
        @map-ready="scheduleMapStateRehydrate"
      />
    </div>

    <!-- UI Overlay Layer -->
    <div class="ui-layer">
      <!-- Header -->
      <header class="dashboard-header glass-panel">
        <div class="header-left">
          <img src="../assets/logo_dark_bg.svg" alt="GeoFusion Logo" class="sys-logo" />
        </div>
        <div class="header-center">
          <h1 class="glow-text">{{ currentProvince === '全国' ? '全国' : currentProvince }}地质灾害风险监测预警</h1>
        </div>
        <div class="header-right">
          <span class="time">{{ currentTime }}</span>
          <!-- 用户菜单组件 -->
          <UserMenu />
        </div>
      </header>

      <div
        v-if="showVendorSyncStrip"
        class="vendor-sync-strip"
        :class="{ error: vendorSyncIsError, idle: vendorSyncIsIdle, busy: vendorSyncIsBusy }"
      >
        <span v-if="vendorSyncIsBusy" class="sync-pulse" aria-hidden="true"></span>
        <span class="vendor-sync-text">{{ vendorSyncBarText }}</span>
        <button
          type="button"
          class="vendor-sync-dismiss"
          aria-label="关闭"
          @click.stop="dismissVendorSyncHint"
        >
          <svg
            class="vendor-sync-dismiss-icon"
            viewBox="0 0 16 16"
            width="14"
            height="14"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              d="M5 5l6 6M11 5l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Main Content Grid (Desktop) -->
      <main class="dashboard-main desktop-layout">
        <!-- Left Panel -->
        <aside class="panel-left">
          <div class="glass-panel wrapper">
            <h2 class="panel-title">综合接入情况</h2>
            <div class="charts-placeholder">
              <div class="stat-box">设备率 <span class="cyan">92%</span></div>
              <div class="stat-box">在线率 <span class="cyan">85%</span></div>
            </div>
            
            <!-- 大屏网格入口按钮 -->
            <button class="grid-nav-btn mt-15" @click="goToGridDashboard">
              多设备视图
            </button>
          </div>
          <div class="glass-panel wrapper flex-grow">
            <h2 class="panel-title">监测站点列表 ({{ filteredStations.length }})</h2>
            <ul class="device-list">
              <li v-for="(station, index) in filteredStations" :key="index" :class="['device-item', station.status]"
                style="cursor: pointer;" @click.stop="handleStationListItemClick(station)">
                <span class="device-name">{{ station.name }}</span>
                <span class="device-type-tag">{{ station.device_type === 'camera' ? '监控' : '监测' }}</span>
                <span class="device-status">{{ getStatusText(station.status) }}</span>
              </li>
            </ul>
          </div>
        </aside>

        <!-- Center Space (Empty) to let map show through -->
        <section class="panel-center empty-space"></section>

        <!-- Right Panel -->
        <aside class="panel-right">
          <div class="glass-panel wrapper">
            <h2 class="panel-title">实时天气</h2>
            <div class="weather-widget">
              <div class="weather-location">{{ weatherData.location }}</div>
              <div class="weather-main">
                <span class="weather-icon">{{ weatherData.icon }}</span>
                <span class="weather-temp">{{ weatherData.temperature }}°C</span>
              </div>
              <div class="weather-desc">{{ weatherData.description }}</div>
              <div class="weather-details">
                <div class="detail-item">
                  <span class="label">湿度</span>
                  <span class="val">{{ weatherData.humidity }}%</span>
                </div>
                <div class="detail-item">
                  <span class="label">风速</span>
                  <span class="val">{{ weatherData.windSpeed }} km/h</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 选中的设备简报卡片 -->
          <div v-if="selectedStationData" class="glass-panel wrapper selected-device-card animate-slide-in">
            <div class="card-header-row">
              <h2 class="panel-title">当前选中设备</h2>
              <button class="close-card-btn" @click.stop="closeSelectedCard">×</button>
            </div>
            <div class="device-info-brief">
              <div class="brief-row">
                <span class="label">名称:</span>
                <span class="value">{{ selectedStationData.name }}</span>
              </div>
              <div class="brief-row">
                <span class="label">状态:</span>
                <span :class="['value', 'status-tag', selectedStationData.status]">
                  {{ getStatusText(selectedStationData.status) }}
                </span>
              </div>
              <div class="brief-row">
                <span class="label">编号:</span>
                <span class="value sid">{{ selectedStationData.sid_no }}</span>
              </div>
              <div class="brief-row">
                <span class="label">类型:</span>
                <span class="value">{{ selectedStationData.device_type === 'camera' ? '监控设备' : '监测设备' }}</span>
              </div>
            </div>
            <button class="monitor-nav-btn" @click="navigateToMonitor(selectedStationData.sid_no)">
              进入实时监控 <span class="icon">→</span>
            </button>

          </div>

          <div v-else class="glass-panel wrapper flex-grow">
            <h2 class="panel-title">项目分组</h2>
            <ul class="project-list">
              <li v-for="project in projectStats" :key="project.name" 
                  :class="['project-item', project.status, { active: selectedProject === project.name }]"
                  @click="handleProjectClick(project)">
                <span class="project-name">{{ project.name }}</span>
                <span class="station-count">{{ project.count }}个站点</span>
              </li>
            </ul>
          </div>
        </aside>
      </main>
    </div>

    <!-- Floating Map Mode Switcher -->
    <div class="map-controls-overlay">
      <MapModeSwitcher v-model="baseMapMode" />
    </div>

    <!-- Modern Mobile Bottom Drawer -->
    <div class="mobile-drawer" :class="{ 'drawer-open': mobileDrawerOpen }">
      <div class="drawer-handle-area" @click="mobileDrawerOpen = !mobileDrawerOpen">
        <div class="capsule-handle"></div>
      </div>
      <div class="drawer-content">
        <div class="drawer-scroll-area">
          <div class="drawer-section modern-stats">
            <h2 class="drawer-title">综合接入情况</h2>
            <div class="drawer-stats">
              <div class="stat-box glass-card">
                <span class="stat-label">设备率</span>
                <span class="stat-value cyan">92%</span>
              </div>
              <div class="stat-box glass-card">
                <span class="stat-label">在线率</span>
                <span class="stat-value cyan">85%</span>
              </div>
            </div>
          </div>
          
          <div class="drawer-section weather-section">
            <h2 class="drawer-title">实时天气 · {{ weatherData.location }}</h2>
            <div class="drawer-weather glass-card">
              <div class="weather-left">
                <span class="weather-icon">{{ weatherData.icon }}</span>
                <span class="weather-temp">{{ weatherData.temperature }}°C</span>
              </div>
              <span class="weather-desc">{{ weatherData.description }}</span>
            </div>
          </div>

          <div class="drawer-section list-section">
            <h2 class="drawer-title">监测站点 ({{ filteredStations.length }})</h2>
            <ul class="device-list">
              <li v-for="project in groupedStations" :key="project.name" class="project-group">
                <div class="project-header glass-card" @click="handleProjectClick(project)">
                  <div class="proj-header-left">
                    <span class="toggle-icon">{{ expandedProjects.includes(project.name) ? '▼' : '▶' }}</span>
                    <span class="project-name">{{ project.name }}</span>
                  </div>
                  <span class="station-count">{{ project.stations.length }} 台设备</span>
                </div>
                <ul v-show="expandedProjects.includes(project.name)" class="project-stations">
                  <li v-for="(station, index) in project.stations" :key="index" :class="['device-item', station.status]"
                    @click.stop="handleStationListItemClick(station)">
                    <span class="device-type-dot" :class="station.device_type"></span>
                    <span class="device-name">{{ station.name }}</span>
                    <span class="device-status">{{ getStatusText(station.status) }}</span>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import request from '../utils/request'
import MapComponent from '../components/MapComponent.vue'
import MobileMapComponent from '../components/MobileMapComponent.vue'
import TiandituJsSatelliteMapComponent from '../components/TiandituJsSatelliteMapComponent.vue'
import UserMenu from '../components/UserMenu.vue'
import MapModeSwitcher from '../components/MapModeSwitcher.vue'
import { TIANDITU_JS_DETAIL_ZOOM_THRESHOLD } from '../utils/stationCluster'

export default {
  name: 'DashboardView',
  components: {
    MapComponent,
    MobileMapComponent,
    TiandituJsSatelliteMapComponent,
    UserMenu,
    MapModeSwitcher
  },
  data() {
    return {
      currentTime: new Date().toLocaleTimeString(),
      mobileDrawerOpen: false,
      currentProvince: '吉林省',
      baseMapMode: 'outline', // 'outline' | 'satellite'
      selectedProject: null, // 当前选中的项目
      selectedStationData: null, // 当前选中的站点数据
      envelopeDeviceLevelMap: {}, // sid_no -> normal/yellow/orange/red
      envelopeGroupLevelMap: {}, // group_name -> normal/yellow/orange/red
      envelopeGroupIdLevelMap: {}, // group_id(string) -> normal/yellow/orange/red
      // 大屏默认取近 7 天（很多设备数据不连续，24h 会导致 summary 为空）
      envelopeTimeRange: '7d',
      expandedProjects: [],
      weatherData: {
        location: '吉林省',
        temperature: '--',
        icon: '⌛',
        description: '加载中...',
        humidity: '--',
        windSpeed: '--'
      },
      stations: [],
      vendorSyncStatus: null,
      vendorSyncPollTimer: null,
      /** 用户关闭厂商同步条时写入的签名，后台文案/进度变化后再显示 */
      vendorSyncHintDismissedFor: null
    }
  },
  computed: {
    vendorSyncIsError() {
      return !!(this.vendorSyncStatus && this.vendorSyncStatus.error_message)
    },
    vendorSyncIsIdle() {
      const p = this.vendorSyncStatus && this.vendorSyncStatus.progress
      return !this.vendorSyncIsError && p && p.phase === 'idle'
    },
    vendorSyncIsBusy() {
      if (!this.vendorSyncStatus || this.vendorSyncIsError) return false
      const p = this.vendorSyncStatus.progress || {}
      return ['cycle', 'devices', 'measurements'].includes(p.phase)
    },
    vendorSyncBarText() {
      return this.formatVendorSyncLine(this.vendorSyncStatus)
    },
    vendorSyncStatusSignature() {
      const s = this.vendorSyncStatus
      const p = s && s.progress ? { ...s.progress } : null
      return JSON.stringify({
        err: s && s.error_message,
        msg: s && s.current_message,
        prog: p
      })
    },
    showVendorSyncStrip() {
      return !!(this.vendorSyncBarText && this.vendorSyncStatusSignature !== this.vendorSyncHintDismissedFor)
    },
    currentMapComponent() {
      if (this.baseMapMode === 'satellite') return 'TiandituJsSatelliteMapComponent'
      
      // Both outline and outline3d use MapComponent (Desktop) or MobileMapComponent
      return this.$device?.isTablet()
        ? 'MobileMapComponent'
        : (this.$device?.isMobile() ? 'MobileMapComponent' : 'MapComponent')
    },
    filteredStations() {
      let list = this.stations
      if (this.currentProvince && this.currentProvince !== '全国') {
        list = list.filter(s => s.province === this.currentProvince)
      }
      if (this.selectedProject) {
        list = list.filter(s => (s.project_name || '默认分组') === this.selectedProject)
      }
      return list
    },
    projectStats() {
      const stats = {};
      const baseList = (!this.currentProvince || this.currentProvince === '全国') 
          ? this.stations 
          : this.stations.filter(s => s.province === this.currentProvince);

      baseList.forEach(s => {
        const pName = s.project_name || '默认分组';
        if (!stats[pName]) {
          const gid = s.group_id != null ? String(s.group_id) : ''
          stats[pName] = {
            name: pName,
            group_id: gid,
            count: 0,
            status: 'normal',
            stations: []
          };
          // 如果 summary 里有组级别，作为底色（后续再和设备冒泡取 max）
          const glv =
            (gid && this.envelopeGroupIdLevelMap && this.envelopeGroupIdLevelMap[gid]) ||
            (this.envelopeGroupLevelMap && this.envelopeGroupLevelMap[pName])
          if (glv) stats[pName].status = this.mapLevelToStatus(glv)
        }
        // 若首条设备没有 group_id，但后续设备有，则补齐 group_id 并重新应用组级别底色
        if ((!stats[pName].group_id || stats[pName].group_id === '') && s.group_id != null) {
          const gid2 = String(s.group_id)
          stats[pName].group_id = gid2
          const glv2 =
            (this.envelopeGroupIdLevelMap && this.envelopeGroupIdLevelMap[gid2]) ||
            (this.envelopeGroupLevelMap && this.envelopeGroupLevelMap[pName])
          if (glv2) stats[pName].status = this.mapLevelToStatus(glv2)
        }
        stats[pName].count++;
        stats[pName].stations.push(s);

        // 状态冒泡：红 > 橙 > 黄 > 正常（任一指标取最大）
        const rank = this.severityRank(s.status)
        const curRank = this.severityRank(stats[pName].status)
        if (rank > curRank) stats[pName].status = s.status
      });
      return Object.values(stats).sort((a, b) => a.name.localeCompare(b.name));
    },
    groupedStations() {
      return this.projectStats.map(item => ({
        name: item.name,
        status: item.status,
        stations: item.stations || []
      }))
    }
  },
  watch: {
    baseMapMode() {
      // component switched, re-apply selected province to new map instance
      this.scheduleMapStateRehydrate()
    },
    currentProvince() {
      // keep map state in sync when province changes from any source
      this.scheduleMapStateRehydrate()
    }
  },
  mounted() {
    this.fetchStations().then(() => {
      // 恢复状态逻辑
      const query = this.$route.query;
      if (query.province) {
        this.currentProvince = query.province;
      }
      if (query.project) {
        this.selectedProject = query.project;
        // 如果有项目，尝试缩放到项目
        const project = this.projectStats.find(p => p.name === query.project);
        if (project && this.$refs.mapRef && typeof this.$refs.mapRef.zoomToProjectCluster === 'function') {
           this.$nextTick(() => {
             this.$refs.mapRef.zoomToProjectCluster(project.stations);
           });
        }
      }
      if (query.station) {
        const station = this.stations.find(s => s.sid_no === query.station);
        if (station) {
          this.selectedStationData = station;
          // 如果有具体站点，缩放到站点
          if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToStation === 'function') {
             this.$nextTick(() => {
               this.$refs.mapRef.zoomToStation(station);
             });
          }
        }
      }

      // Ensure initial map instance focuses current selected province.
      this.scheduleMapStateRehydrate();
    });

    this.timer = setInterval(() => {
      this.currentTime = new Date().toLocaleTimeString()
    }, 1000)

    // 初始加载吉林省天气 (不启用逆地理编码，保持显示“吉林省”)
    this.fetchWeather(43.8435, 126.5530, false);

    this.fetchVendorSyncStatus()
    this.vendorSyncPollTimer = setInterval(() => this.fetchVendorSyncStatus(), 8000)
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.vendorSyncPollTimer) {
      clearInterval(this.vendorSyncPollTimer)
    }
  },
  methods: {
    severityRank(level) {
      const map = { normal: 0, yellow: 1, orange: 2, red: 3 }
      return map[level] ?? 0
    },
    mapLevelToStatus(level) {
      const l = (level || 'normal').toLowerCase()
      if (['red', 'orange', 'yellow'].includes(l)) return l
      return 'normal'
    },
    dismissVendorSyncHint() {
      this.vendorSyncHintDismissedFor = this.vendorSyncStatusSignature
    },
    formatVendorSyncLine(raw) {
      if (!raw) return ''
      if (raw.error_message) {
        return `厂商数据同步异常：${raw.error_message}`
      }
      const parts = []
      if (raw.current_message) parts.push(raw.current_message)
      const p = raw.progress || {}
      if (p.phase === 'devices' && p.total != null && p.done != null) {
        parts.push(`（设备 ${p.done}/${p.total}）`)
      }
      if (p.phase === 'measurements') {
        const seg = []
        if (p.project_index != null && p.project_total != null) {
          seg.push(`项目 ${p.project_index}/${p.project_total}`)
        }
        if (typeof p.rows_inserted === 'number') seg.push(`已写入 ${p.rows_inserted} 条`)
        if (p.batches_done) seg.push(`${p.batches_done} 批`)
        if (seg.length) parts.push(`（${seg.join(' · ')}）`)
      }
      if (p.phase === 'idle' && raw.last_success_time) {
        const t = String(raw.last_success_time).replace('T', ' ').slice(0, 19)
        parts.push(`（最近成功 ${t}）`)
      }
      return parts.filter(Boolean).join('')
    },
    async fetchVendorSyncStatus() {
      try {
        const res = await request.get('/devices/sync-status/')
        this.vendorSyncStatus = res.data || null
      } catch (e) {
        console.warn('sync-status poll failed', e)
      }
    },
    scheduleMapStateRehydrate() {
      this.$nextTick(() => {
        const map = this.$refs.mapRef
        if (!map) return
        const provinceName = this.currentProvince || '全国'
        if (this.selectedStationData && typeof map.zoomToStation === 'function') {
          map.zoomToStation(this.selectedStationData)
          return
        }
        if (this.selectedProject && typeof map.zoomToProjectCluster === 'function') {
          const project = this.projectStats.find(p => p.name === this.selectedProject)
          if (project) {
            map.zoomToProjectCluster(project.stations)
            return
          }
        }
        if (typeof map.syncViewState === 'function') {
          map.syncViewState({ provinceName })
          return
        }
        if (typeof map.zoomToProvince === 'function') {
          map.zoomToProvince(provinceName)
        }
      })
    },
    toggleBaseMap() {
      // Deprecated in favor of MapModeSwitcher
    },
    goToGridDashboard() {
      this.$router.push({ name: 'DashboardGrid' })
    },
    closeSelectedCard() {
      this.selectedStationData = null;
    },
    async fetchStations() {
      try {
        const [devicesResp, summaryResp] = await Promise.all([
          request.get('/devices/list/'),
          request.get('/devices/envelope-summary/', { params: { time_range: this.envelopeTimeRange } }),
        ])

        const summary = summaryResp?.data || {}
        const devLevels = {}
        const grpLevels = {}
        const grpIdLevels = {}
        if (Array.isArray(summary.devices)) {
          summary.devices.forEach((d) => {
            if (d && d.device_sid) devLevels[d.device_sid] = d.max_alert_level || 'normal'
          })
        }
        if (Array.isArray(summary.groups)) {
          summary.groups.forEach((g) => {
            if (g && g.group_name) grpLevels[g.group_name] = g.max_alert_level || 'normal'
            if (g && g.group_id != null) grpIdLevels[String(g.group_id)] = g.max_alert_level || 'normal'
          })
        }
        this.envelopeDeviceLevelMap = devLevels
        this.envelopeGroupLevelMap = grpLevels
        this.envelopeGroupIdLevelMap = grpIdLevels

        const response = devicesResp
        if (response.data && Array.isArray(response.data)) {
          this.stations = response.data.map(device => {
            const sid = device.sid_no
            const lv = this.envelopeDeviceLevelMap[sid] || 'normal'
            return ({
            name: device.name || device.sid_no,
            value: [device.longitude, device.latitude, 10], // Default weight 10
            status: this.mapLevelToStatus(lv),
            province: '吉林省', // Default province for now
            sid_no: device.sid_no, // Keep original ID
            project_name: device.group_name || device.project_name || '默认分组',
            group_id: device.group_id ?? null,
            device_type: device.device_type || 'sensor'
          })
          });
        }
      } catch (error) {
        console.error('Failed to fetch stations:', error);
      }
    },
    getStatusText(statusCode) {
      const map = {
        'normal': '正常',
        'yellow': '黄色预警',
        'orange': '橙色预警',
        'red': '红色预警'
      }
      return map[statusCode] || '未知状态';
    },
    handleProvinceClick(payload) {
      this.currentProvince = payload.name;
      this.selectedProject = null; // 重置项目选择
      this.weatherData.location = payload.name === '全国' ? '中国' : payload.name;

      if (payload.center) {
        this.fetchWeather(payload.center[1], payload.center[0], false);
      }
    },
    handleStationClick(payload) {
      this.weatherData.location = payload.name;
      if (payload.gps) {
        this.fetchWeather(payload.gps[1], payload.gps[0], true);
      }
      
      const station = this.stations.find(s => s.name === payload.name);
      if (station) {
         this.selectedStationData = station; // 设置选中设备数据
         const pname = station.project_name || '默认分组';
         if (this.selectedProject !== pname) {
             this.selectedProject = pname;
         }
         
         // 自动平移地图到该设备
         if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToStation === 'function') {
            this.$refs.mapRef.zoomToStation(station);
         }
      }
    },
    navigateToMonitor(sid) {
      this.$router.push({ name: 'Monitor', params: { sid } })
    },
    handleClusterClick(payload) {
      if (payload.projectName) {
        this.selectedProject = payload.projectName;
      }
    },
    handleStationListItemClick(station) {
      // 1. 更新天气和UI状态
      this.handleStationClick({
        name: station.name,
        gps: [station.value[0], station.value[1]]
      });

      // 2. 自动关联选中项目（先于地图同步，保证 filteredStations 与 ref 一致）
      const pname = station.project_name || '默认分组';
      if (this.selectedProject !== pname) {
        this.selectedProject = pname;
      }

      // 3. 再次缩放到仪器（handleStationClick 已调一次；nextTick 避免遥感下 ref/数据未稳导致「缩放不动」）
      this.$nextTick(() => {
        if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToStation === 'function') {
          this.$refs.mapRef.zoomToStation(station);
        }
        this.scheduleMapStateRehydrate();
      });
    },
    handleProjectClick(project) {
      if (project?.name) {
        if (this.expandedProjects.includes(project.name)) {
          this.expandedProjects = this.expandedProjects.filter(name => name !== project.name)
        } else {
          this.expandedProjects.push(project.name)
        }
      }
      if (this.selectedProject === project.name) {
        // 检查当前缩放级别
        const map = this.$refs.mapRef;
        const z = map && typeof map.currentZoom === 'number' ? map.currentZoom : null;
        const isSatellite = this.currentMapComponent === 'TiandituJsSatelliteMapComponent';
        const isProvinceView = typeof z === 'number' ? (isSatellite ? z <= 5 : z <= 10) : false;
        const isDetailView = typeof z === 'number'
          ? (isSatellite ? z >= TIANDITU_JS_DETAIL_ZOOM_THRESHOLD : z > 100)
          : false;
        if (map && typeof map.zoomToProjectCluster === 'function' && (isProvinceView || isDetailView)) {
          map.zoomToProjectCluster(project.stations);
          return;
        }

        // 否则（已经在聚合视图），取消选中并返回省份视图
        this.selectedProject = null;
        
        // 恢复到省份视图
        if (map && typeof map.zoomToProvince === 'function') {
          map.zoomToProvince(this.currentProvince);
        }
      } else {
        // 选中项目
        this.selectedProject = project.name;
        
        // 缩放到项目聚合视图
        if (this.$refs.mapRef && typeof this.$refs.mapRef.zoomToProjectCluster === 'function') {
          this.$refs.mapRef.zoomToProjectCluster(project.stations);
        }
      }
    },
    async fetchWeather(lat, lon, shouldGeocode = true) {
      try {
        // Fetch weather and reverse geocoding in parallel (if needed)
        const weatherPromise = axios.get(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&hourly=relative_humidity_2m,wind_speed_10m`);

        let geoPromise = Promise.resolve(null);
        if (shouldGeocode) {
          geoPromise = axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=zh-CN`).catch(() => null);
        }

        const [weatherRes, geoRes] = await Promise.all([weatherPromise, geoPromise]);

        if (weatherRes.data && weatherRes.data.current_weather) {
          const cw = weatherRes.data.current_weather;
          this.weatherData.temperature = Math.round(cw.temperature);
          this.weatherData.icon = this.getWeatherIcon(cw.weathercode);
          this.weatherData.description = this.getWeatherDesc(cw.weathercode);
          this.weatherData.windSpeed = cw.windspeed;

          // Get humidity from the closest hourly data point
          if (weatherRes.data.hourly && weatherRes.data.hourly.relative_humidity_2m) {
            this.weatherData.humidity = weatherRes.data.hourly.relative_humidity_2m[0];
          }
        }

        if (geoRes && geoRes.data && geoRes.data.address) {
          const addr = geoRes.data.address;
          const city = addr.city || addr.town || addr.village || addr.county || '';
          const suburb = addr.suburb || addr.district || '';
          if (city || suburb) {
            this.weatherData.location = `${city}${suburb ? ' · ' + suburb : ''}`;
          }
        }
      } catch (err) {
        console.error('Weather fetch failed:', err);
        this.weatherData.description = '获取失败';
      }
    },
    getWeatherIcon(code) {
      const icons = {
        0: '☀️',
        1: '🌤️', 2: '⛅', 3: '☁️',
        45: '🌫️', 48: '🌫️',
        51: '🌧️', 53: '🌧️', 55: '🌧️',
        61: '🌧️', 63: '🌧️', 65: '🌧️',
        71: '❄️', 73: '❄️', 75: '❄️',
        80: '🌦️', 81: '🌦️', 82: '🌦️',
        95: '⛈️', 96: '⛈️', 99: '⛈️'
      };
      return icons[code] || '🌡️';
    },
    getWeatherDesc(code) {
      const descs = {
        0: '晴朗',
        1: '晴间多云', 2: '多云', 3: '阴天',
        45: '有雾', 48: '浓雾',
        51: '毛毛雨', 53: '细雨', 55: '小雨',
        61: '阵雨', 63: '中雨', 65: '大雨',
        71: '小雪', 73: '中雪', 75: '大雪',
        80: '阵雷雨', 81: '雷阵雨', 82: '雷暴',
        95: '雷阵雨', 96: '雷阵雨伴有冰雹', 99: '强雷阵雨伴有冰雹'
      };
      return descs[code] || '未知';
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  position: relative;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #020617 0%, #0f172a 100%);
  overflow: hidden;
}

.map-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  /* Background layer */
}

.calibrate-btn {
  margin-top: 8px;
}

.ui-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
  z-index: 10;
  /* Put UI on top of map */
  pointer-events: none;
  /* Let clicks pass through to map where UI is transparent */
}

/* Re-enable pointer events for actual UI panels so buttons/scrolls work */
.dashboard-header,
.panel-left,
.panel-right {
  pointer-events: auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0.8rem 1rem;
  flex: 0 0 auto;
  position: relative;
  overflow: visible !important;
  z-index: 100;
}

.dashboard-header h1 {
  margin: 0;
  font-size: clamp(1.4rem, 2vw, 1.8rem);
  font-weight: 700;
  letter-spacing: 2px;
  white-space: nowrap;
}

.vendor-sync-strip {
  --vendor-sync-row-h: 26px;
  flex: 0 0 auto;
  align-self: center;
  max-width: min(96vw, 920px);
  margin: -1.2rem auto 0.6rem;
  padding: 7px 6px 7px 14px;
  border-radius: 999px;
  font-size: 0.78rem;
  line-height: 1;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(56, 189, 248, 0.4);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 99;
  pointer-events: none;
}

.vendor-sync-strip.idle {
  opacity: 0.9;
  border-color: rgba(148, 163, 184, 0.35);
}

.vendor-sync-strip.busy {
  border-color: rgba(34, 211, 238, 0.55);
}

.vendor-sync-strip.error {
  border-color: rgba(248, 113, 113, 0.65);
  color: #fecaca;
}

.vendor-sync-text {
  flex: 1;
  min-width: 0;
  min-height: var(--vendor-sync-row-h);
  line-height: var(--vendor-sync-row-h);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vendor-sync-dismiss {
  flex: 0 0 auto;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--vendor-sync-row-h);
  height: var(--vendor-sync-row-h);
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.8;
  pointer-events: auto;
}

.vendor-sync-dismiss-icon {
  display: block;
  flex-shrink: 0;
  /* 矢量对齐像素格，避免文字 × 发糊、偏格 */
}

.vendor-sync-dismiss:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.12);
}

.sync-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22d3ee;
  flex-shrink: 0;
  animation: vendor-sync-pulse 1.25s ease-in-out infinite;
}

@keyframes vendor-sync-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.sys-logo {
  height: 40px;
  width: auto;
  filter: drop-shadow(0 0 8px rgba(0, 240, 255, 0.5));
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
}

.time {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--accent-cyan);
  letter-spacing: 1px;
}

.basemap-toggle-btn {
  display: none;
}

.grid-nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 999px; /* 圆角胶囊 */
  background: linear-gradient(135deg, rgba(0, 240, 255, 0.1) 0%, rgba(14, 165, 233, 0.2) 100%);
  border: 1px solid rgba(0, 240, 255, 0.4);
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.15), inset 0 0 10px rgba(0, 240, 255, 0.1);
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.8);
}

.grid-nav-btn::before {
  content: '⊞'; /* 使用网格符号代替普通的文字 */
  font-size: 1.1rem;
  color: #00f0ff;
}

.grid-nav-btn:hover {
  background: linear-gradient(135deg, rgba(0, 240, 255, 0.25) 0%, rgba(14, 165, 233, 0.35) 100%);
  border-color: #00f0ff;
  box-shadow: 0 0 25px rgba(0, 240, 255, 0.3), inset 0 0 15px rgba(0, 240, 255, 0.2);
  transform: translateY(-1px);
}

.grid-nav-btn:active {
  transform: translateY(1px);
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.2);
}

.mt-15 {
  margin-top: 15px;
  width: 100%;
  justify-content: center;
}

.glow-text {
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.6), 0 0 20px rgba(0, 240, 255, 0.3);
}

.dashboard-main {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100vh - 80px - 2rem);
  box-sizing: border-box;
}

/* 移动端地图容器高度优化 */
@media screen and (max-width: 768px) {
  .map-layer {
    height: calc(100vh - 120px);
  }

  .mobile-map-container {
    height: 100% !important;
  }
}

.panel-left,
.panel-right {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  height: 100%;
  z-index: 1;
}

.glass-panel {
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 240, 255, 0.15);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}

.glass-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.5), transparent);
}

.wrapper {
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
}

.wrapper.flex-grow {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}



.panel-title {
  font-size: 1.1rem;
  margin-top: 0;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-main);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.charts-placeholder {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.stat-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 0.8rem 1rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.9rem;
}

.cyan {
  color: var(--accent-cyan);
  font-weight: bold;
  font-size: 1.2rem;
}

.alert-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.alert-item {
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  border-left: 3px solid;
  font-size: 0.9rem;
}

.alert-item.error {
  border-left-color: var(--accent-red);
  color: #fca5a5;
}

.alert-item.warning {
  border-left-color: var(--accent-amber);
  color: #fde047;
}

.map-controls-overlay {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  pointer-events: auto;
}

/* Adjust for selected device card if it's too large or overlaps */
.selected-device-card {
  z-index: 60;
}
.weather-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.weather-location {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--accent-cyan);
  margin-bottom: 0.8rem;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.3);
}

.weather-main {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 0.5rem;
}

.weather-icon {
  font-size: 2.8rem;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.2));
}

.weather-temp {
  font-size: 2.4rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -1px;
}

.weather-desc {
  font-size: 1.1rem;
  color: #cadbee;
  margin-bottom: 1.2rem;
  font-weight: 500;
}

.weather-details {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 1.5rem;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

.detail-item .label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item .val {
  font-size: 1rem;
  color: var(--accent-cyan);
  font-weight: 600;
}

.device-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.device-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.85rem;
  border-left: 3px solid;
}

.device-item.normal {
  border-left-color: #10b981;
  color: #fff;
}

.device-item.normal .device-status {
  color: #10b981;
}

.device-item.yellow {
  border-left-color: #eab308;
  color: #fff;
}

.device-item.yellow .device-status {
  color: #eab308;
}

.device-item.orange {
  border-left-color: #f97316;
  color: #fff;
}

.device-item.orange .device-status {
  color: #f97316;
}

.device-item.red {
  border-left-color: var(--accent-red);
  color: #fff;
}

.device-item.red .device-status {
  color: var(--accent-red);
}

.device-name {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-type-tag {
  flex-shrink: 0;
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  min-width: 2.2em;
  text-align: center;
}

.device-status {
  flex-shrink: 0;
}

/* 统一美化滚动条 */
.alert-list::-webkit-scrollbar,
.device-list::-webkit-scrollbar {
  width: 6px;
}

.alert-list::-webkit-scrollbar-track,
.device-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.alert-list::-webkit-scrollbar-thumb,
.device-list::-webkit-scrollbar-thumb {
  background: rgba(0, 240, 255, 0.3);
  border-radius: 4px;
}

.alert-list::-webkit-scrollbar-thumb:hover,
.device-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 240, 255, 0.6);
}

/* ========== 移动端现代抽屉样式 ========== */
.mobile-drawer {
  display: none;
}

/* ========== 移动端响应式样式 ========== */
@media screen and (max-width: 768px) {
  /* 隐藏桌面端布局 */
  .desktop-layout {
    display: none !important;
  }

  /* 地图全屏 */
  .map-layer {
    height: 100vh;
  }

  /* UI层不要拦截触摸 */
  .ui-layer {
    padding: 0;
    pointer-events: none;
  }

  .dashboard-header {
    display: grid;
    grid-template-areas: 
      "left right"
      "center center";
    grid-template-columns: 1fr auto;
    padding: 12px 16px;
    margin-bottom: 0;
    pointer-events: auto;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.5) 100%);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    gap: 12px;
  }

  .header-left {
    grid-area: left;
    display: flex;
    align-items: center;
  }

  .header-right {
    grid-area: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .header-center {
    grid-area: center;
    position: static;
    transform: none;
    width: 100%;
    text-align: center;
  }

  .vendor-sync-strip {
    margin: 0.5rem auto 0.5rem; /* Cancel out the negative margin from desktop */
    max-width: 90vw;
  }

  .map-controls-overlay {
    bottom: calc(40px + 1.5rem);
    transition: bottom 0.3s ease;
  }

  /* When drawer opens, push controls even higher or hide them? For now, let's keep it simple by just keeping them accessible above the handle. */

  .dashboard-header h1 {
    font-size: 1.1rem;
    letter-spacing: 0.5px;
    margin: 0;
  }

  .sys-logo {
    height: 28px;
  }

  .time {
    font-size: 0.85rem;
  }

  /* 现代底部抽屉 */
  .mobile-drawer {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: rgba(15, 23, 42, 0.85);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px 24px 0 0;
    transform: translateY(calc(100% - 40px));
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: auto;
    box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5);
    max-height: 85vh;
  }

  .mobile-drawer.drawer-open {
    transform: translateY(0);
  }

  .drawer-handle-area {
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
    flex-shrink: 0;
  }

  .capsule-handle {
    width: 40px;
    height: 5px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    transition: background 0.3s;
  }

  .mobile-drawer.drawer-open .capsule-handle {
    background: var(--accent-cyan);
    box-shadow: 0 0 8px rgba(0, 240, 255, 0.5);
  }

  .drawer-content {
    flex: 1;
    overflow: hidden;
    position: relative;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .drawer-scroll-area {
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .drawer-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .drawer-title {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin: 0;
    padding-left: 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }

  /* 抽屉内部的高级玻璃卡片 */
  .glass-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 16px;
    transition: background 0.2s;
  }

  .drawer-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .drawer-stats .stat-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px;
  }

  .stat-label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  .drawer-weather {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .weather-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .weather-icon {
    font-size: 2rem;
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.2));
  }

  .weather-temp {
    font-size: 1.6rem;
    font-weight: 800;
    color: #fff;
  }

  .weather-desc {
    color: var(--accent-cyan);
    font-size: 0.95rem;
    font-weight: 500;
  }

  .project-header.glass-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    margin-bottom: 8px;
    cursor: pointer;
    background: rgba(0, 240, 255, 0.03);
    border-color: rgba(0, 240, 255, 0.15);
  }

  .proj-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .project-name {
    font-weight: 600;
    color: #fff;
    font-size: 0.95rem;
  }

  .device-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .project-stations {
    margin: 0 0 16px 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 12px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    margin-left: 12px;
  }

  .device-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    gap: 10px;
    border: 1px solid transparent;
  }

  .device-type-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  
  .device-type-dot.camera { background: #3b82f6; box-shadow: 0 0 8px #3b82f6; }
  .device-type-dot.sensor { background: #10b981; box-shadow: 0 0 8px #10b981; }

  .device-item.normal { border-left: 3px solid #10b981; }
  .device-item.yellow { border-left: 3px solid #eab308; }
  .device-item.orange { border-left: 3px solid #f97316; }
  .device-item.red { border-left: 3px solid var(--accent-red); }

  .device-item .device-name {
    font-size: 0.9rem;
  }
}

/* 平板端适配 */
@media screen and (min-width: 769px) and (max-width: 1024px) {
  .dashboard-header {
    padding: 12px 20px;
  }
  
  .dashboard-header h1 {
    font-size: 1.3rem;
  }

  .panel-left,
  .panel-right {
    width: 260px;
    gap: 1rem;
  }
  
  .wrapper {
    padding: 1rem;
  }
  
  .panel-title {
    font-size: 1rem;
  }
  
  .stat-box {
    padding: 0.6rem;
    font-size: 0.8rem;
  }

  .device-item {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
  
  .weather-temp {
    font-size: 1.8rem;
  }
}

/* 小屏幕手机进一步简化 */
@media screen and (max-width: 480px) {
  .dashboard-header {
    padding: 8px 12px;
  }
  
  .dashboard-header h1 {
    font-size: 0.9rem;
    letter-spacing: 0;
  }

  .sys-logo {
    height: 20px;
  }

  .header-left, .header-right {
    gap: 10px;
  }
}

/* ================== 项目分组列表样式 ================== */
.project-list {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.project-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  margin-bottom: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  border-left: 3px solid transparent;
  color: #94a3b8;
}

.project-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.project-item.active {
  background: rgba(0, 240, 255, 0.1);
  border-left-color: var(--accent-cyan);
  color: #fff;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.1);
}

.project-item .project-name {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-item .station-count {
  flex-shrink: 0;
  font-size: 0.8rem;
  opacity: 0.7;
}

/* 状态颜色指示 */
.project-item.yellow { border-left-color: #eab308; }
.project-item.orange { border-left-color: #f97316; }
.project-item.red { border-left-color: var(--accent-red); }

/* 滚动条美化 */
.project-list::-webkit-scrollbar {
  width: 6px;
}

.project-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.project-list::-webkit-scrollbar-thumb {
  background: rgba(0, 240, 255, 0.3);
  border-radius: 4px;
}

.project-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 240, 255, 0.6);
}

.selected-device-card {
  margin-top: 1rem;
  background: rgba(0, 240, 255, 0.05);
  border: 1px solid rgba(0, 240, 255, 0.3);
  animation: slideIn 0.3s ease-out;
}

.device-info-brief {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.brief-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  min-height: 1.5em;
}

.brief-row .label {
  color: #94a3b8;
  flex-shrink: 0;
  min-width: 3em;
}
.brief-row .value {
  color: #fff;
  font-weight: 500;
  text-align: right;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.brief-row .sid { font-family: monospace; color: var(--accent-cyan); }

.status-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
}
.status-tag.normal { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.status-tag.yellow { background: rgba(234, 179, 8, 0.2); color: #eab308; }
.status-tag.orange { background: rgba(249, 115, 22, 0.2); color: #f97316; }
.status-tag.red { background: rgba(239, 68, 68, 0.2); color: #ef4444; }

.monitor-nav-btn {
  width: 100%;
  padding: 10px;
  background: linear-gradient(90deg, rgba(0, 240, 255, 0.1), rgba(0, 240, 255, 0.2));
  border: 1px solid var(--accent-cyan);
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.monitor-nav-btn:hover {
  background: rgba(0, 240, 255, 0.3);
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.4);
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header-row .panel-title {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.close-card-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: all 0.2s;
}

.close-card-btn:hover {
  color: var(--accent-cyan);
  transform: scale(1.1);
}
</style>
